import { ResponsiveChoropleth } from "@nivo/geo";
import { worldCountries } from "../../SupplierAdminDashboard/helpers";
import { Box, Divider, Flex, Text, useDisclosure } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { Card } from "../../../components/Card";
import { CURRENCY_SYMBOLS } from "../../../utils";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { PreviewSuppliersLocationsModal } from "./index";
import { sortLocationsByDescTotalAmount } from "../helpers";
import { FaFilter } from "react-icons/fa";

function SuppliersLocationsMap({ mapData, isDataFiltered = false }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "suppliersLocations"
  });
  const minScale = 80;
  const maxScale = 400;
  const scaleStep = 25;
  const [projectionScale, setProjectionScale] = useState(110);

  const minTranslation = -2;
  const maxTranslation = 2;
  const translationStep = 0.3;
  const coordsMinDiff = 30;
  const [projectionTranslation, setProjectionTranslation] = useState([0.4, 0.7]);

  const [dragStartCoords, setDragStartCoords] = useState({ x: 0, y: 0 });

  const handleZoom = useCallback(event => {
    event.preventDefault();
    if (event.deltaY > 0) {
      // Zoom out
      setProjectionScale(projectionScale - scaleStep > minScale ? projectionScale - scaleStep : minScale);
    } else {
      // Zoom in
      if (projectionScale < maxScale) {
        setProjectionScale(projectionScale + scaleStep < maxScale ? projectionScale + scaleStep : maxScale);
      }
    }
  }, [projectionScale]);

  const handleDragStart = (e) => {
    setDragStartCoords({ x: e.clientX, y: e.clientY });
    const ghost = document.createElement("div");
    // Removing the default partially transparent drag image
    e.dataTransfer.setDragImage(ghost, 0, 0);
  };

  const handleDragEnd = (e) => {
    let newXCoord = projectionTranslation[0];
    let newYCoord = projectionTranslation[1];

    if (dragStartCoords.x < e.clientX && e.clientX - dragStartCoords.x > coordsMinDiff) {
      newXCoord = projectionTranslation[0] + translationStep < maxTranslation ?
        projectionTranslation[0] + translationStep : maxTranslation;
    } else if (dragStartCoords.x > e.clientX && dragStartCoords.x - e.clientX > coordsMinDiff) {
      newXCoord = projectionTranslation[0] - translationStep > minTranslation ?
        projectionTranslation[0] - translationStep : minTranslation;
    }

    if (dragStartCoords.y < e.clientY && e.clientY - dragStartCoords.y > coordsMinDiff) {
      newYCoord = projectionTranslation[1] + translationStep < maxTranslation ?
        projectionTranslation[1] + translationStep : maxTranslation;
    } else if (dragStartCoords.y > e.clientY && dragStartCoords.y - e.clientY > coordsMinDiff) {
      newYCoord = projectionTranslation[1] - translationStep > minTranslation ?
        projectionTranslation[1] - translationStep : minTranslation;
    }

    setProjectionTranslation([newXCoord, newYCoord]);
  };

  useEffect(() => {
    // Used to prevent page scrolling while zooming
    const container = document.getElementById("choroplethContainer");
    if (container) {
      container.addEventListener("wheel", handleZoom, { passive: false });
    }
  }, [handleZoom]);

  const [modalData, setModalData] = useState({});

  const handleClick = (e) => {
    setModalData({
      countryName: e.label,
      countryTotalSpend: e.data.value,
      suppliers: e.data.suppliers
    });
    onLocationsModalOpen();
  };

  const handleCloseModal = () => {
    onLocationsModalClose();
    setModalData({});
  };

  const {
    isOpen: isLocationsModalOpen,
    onOpen: onLocationsModalOpen,
    onClose: onLocationsModalClose
  } = useDisclosure();

  return (
    <>
      <Box
        id={"choroplethContainer"}
        draggable={true}
        height={"60vh"}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        cursor={"grab"}
        marginBlock={4}
        marginInline={8}
        border={"1px solid gray"}
        borderRadius={"md"}
      >
        <ResponsiveChoropleth
          data={mapData}
          features={worldCountries}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          colors="YlOrRd"
          domain={[0, 100000000]}
          onClick={handleClick}
          unknownColor="#444444"
          label="properties.name"
          valueFormat=".2s"
          projectionScale={projectionScale}
          projectionTranslation={projectionTranslation}
          projectionRotation={[0, 0, 0]}
          enableGraticule={true}
          graticuleLineColor="#dddddd"
          borderWidth={0.5}
          borderColor="#152538"
          tooltip={(tooltipObj => {
            const { data, label, value: totalValue } = tooltipObj.feature;
            return data ? <Box position="relative">
              <Card
                minWidth={"250px"}
                padding={3}
              >
                <Flex alignItems={"center"} gap={1}>
                  {isDataFiltered ? <FaFilter color={"green"} opacity="0.7" /> : null}
                  <Text fontSize={"md"}>
                    <b>{label}</b>, {t("totalSpend")}: <b>{totalValue && typeof totalValue !== "undefined" ?
                    CURRENCY_SYMBOLS.GBP : ""}{totalValue?.toLocaleString("en-GB", { maximumFractionDigits: 2 }) ?? "N/A"}</b>
                  </Text>
                </Flex>
                <Divider marginBlock={1} />
                {data.suppliers
                  .sort((a, b) => sortLocationsByDescTotalAmount(a, b))
                  .map((el, index) => {
                    return index < 5 ?
                      <Text key={`${label}-${el.supplier}`} fontSize={"sm"}>
                        {index + 1}. {el.supplier}: <b>{el.totalAmount && typeof el.totalAmount !== "undefined" ?
                        CURRENCY_SYMBOLS.GBP : ""}{el.totalAmount?.toLocaleString(
                        "en-GB", { maximumFractionDigits: 2 }) ?? "N/A"}</b>
                      </Text> : null;
                  })}
                {data.suppliers.length > 5 ?
                  <Text fontWeight={"600"} marginTop={2}>
                    {t("clickToSeeAllLocations")}
                  </Text> : null
                }
              </Card>
            </Box> : null;
          })}
          legends={[
            {
              anchor: "bottom-left",
              direction: "column",
              justify: true,
              translateX: 20,
              translateY: -100,
              itemsSpacing: 0,
              itemWidth: 94,
              itemHeight: 18,
              itemDirection: "left-to-right",
              itemTextColor: "#444444",
              itemOpacity: 0.85,
              symbolSize: 18,
              effects: [
                {
                  on: "hover",
                  style: {
                    itemTextColor: "#000000",
                    itemOpacity: 1
                  }
                }
              ]
            }
          ]}
          // "Required" props that throw console warnings if not explicitly set
          projectionType="mercator"
          graticuleLineWidth={0.5}
          isInteractive={true}
          onMouseEnter={() => {}}
          onMouseMove={() => {}}
          onMouseLeave={() => {}}
          layers={["graticule", "features", "legends"]}
          role={"application"}
          value={(el) => el?.value}
          fillColor={""}
          match="id"
          // end of "required" props
        />
      </Box>
      {modalData && Object.keys(modalData).length > 0 ?
        <PreviewSuppliersLocationsModal
          isOpen={isLocationsModalOpen}
          modalData={modalData}
          onClose={handleCloseModal}
        /> : null
      }
    </>
  );
}

export default SuppliersLocationsMap;